/**
 * Native {@link SessionAudioRecorder}: Premium Session Recording on top of the
 * Capacitor `SessionRecorder` plugin (`services/session-recorder`, production
 * scope).
 *
 * - Encoding comes from `SESSION_RECORDING_AUDIO_FORMAT` (the one constant);
 *   the recording id from the server; the cap from the caller.
 * - No auto-resume: after a call, Siri, a route/mic loss or low storage the
 *   recorder stays paused (snapshot `interruption` keeps the cause) until the
 *   provider taps Resume.
 * - An Ascend call going live while recording pauses capture (`ascend_call`) —
 *   the native layers also detect every call themselves (locked screen).
 * - Local audio lives in the plugin's production directory (app-private,
 *   backup-excluded) until `discardLocal` — called by the uploader once the
 *   server confirmed the upload, or by the UI on discard / withdraw.
 *
 * Content-free: events carry states, reasons and levels only.
 */

import {
  SESSION_RECORDING_AUDIO_FORMAT,
  SESSION_RECORDING_LOCAL_MAX_BYTES,
} from '@shared/sessions/session-recording'
import {
  deleteProductionRecording,
  getRecorderReadiness,
  getRecorderStatus,
  isSessionRecorderAvailable,
  isSessionRecorderUnavailableError,
  listStoredRecordings,
  onRecorderEvent,
  pauseRecorder,
  resumeRecorder,
  startProductionRecorder,
  stopRecorder,
  type RecorderInterruption,
  type RecorderStatus,
} from '../../../services/session-recorder'
import { getVoiceCallSession, subscribeVoiceCallSession } from '../../../stores/voice-call-store'
import { isCallBlockingRecording } from '../../sessions/utils/voice-note-interruption'
import {
  IDLE_RECORDER_SNAPSHOT,
  type OrphanRecording,
  type RecorderEvent,
  type RecorderInterruptionReason,
  type RecorderReadiness,
  type RecorderSnapshot,
  type SessionAudioRecorder,
} from './recorder-port'

/** Free space needed to start (100 min ≈ 25 MB of chunks + the joined copy, with headroom). */
export const SESSION_RECORDING_MIN_FREE_BYTES = 200 * 1024 * 1024
/** Mic meter: at most ~4 updates per second. */
const LEVEL_MIN_INTERVAL_MS = 250
/** RMS dBFS mapped to level 0 (silence floor) … 0 dBFS = level 1. */
const LEVEL_FLOOR_DB = -60

const MESSAGES = {
  unsupported: 'Recording a whole session works in the Ascend app on your phone or tablet.',
  updateApp: 'Update the Ascend app to record sessions.',
  callActive: 'Finish your call before recording.',
  resumeDuringCall: 'Finish the call, then resume recording.',
  busy: 'Another recording is still running on this device. Finish it first.',
  micPermission: 'Allow microphone access for Ascend in Settings to record sessions.',
  lowStorage: 'Free up at least 200 MB on this device to record a session.',
  alreadyHasAudio: 'This recording already has audio on this device.',
  startFailed: 'Couldn’t start recording on this device. Try again.',
  micNotBack: 'The microphone isn’t available yet — we’ll keep trying for a few seconds.',
  nothingRecording: 'Nothing is being recorded.',
  deviceProblem: 'Recording hit a problem on this device.',
} as const

const ACTIVE_STATES: ReadonlySet<RecorderSnapshot['state']> = new Set(['starting', 'recording', 'paused'])

export interface NativeSessionAudioRecorderOptions {
  /** True when the upload queue already owns this recording (so it is not an orphan). */
  isTracked?: (recordingId: string) => boolean | Promise<boolean>
  now?: () => number
}

export interface NativeSessionAudioRecorder extends SessionAudioRecorder {
  readonly kind: 'native'
  /** Detach native listeners (tests / hot reload). */
  dispose(): void
}

function errorCode(err: unknown): string | null {
  const code = (err as { code?: unknown } | null)?.code
  return typeof code === 'string' ? code : null
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

function sameSnapshot(a: RecorderSnapshot, b: RecorderSnapshot): boolean {
  return (
    a.state === b.state &&
    a.recordingId === b.recordingId &&
    a.startedAtMs === b.startedAtMs &&
    a.elapsedAudioSec === b.elapsedAudioSec &&
    a.pauseReason === b.pauseReason &&
    a.interruption === b.interruption &&
    a.capReached === b.capReached &&
    a.lastError === b.lastError
  )
}

/** Normalise the native RMS level (or dBFS) to 0..1 for the meter. */
export function normalizeRecorderLevel(raw: unknown): number {
  const r = (raw && typeof raw === 'object' ? raw : {}) as { db?: unknown; rms?: unknown }
  let db: number | null = typeof r.db === 'number' && Number.isFinite(r.db) ? r.db : null
  if (db == null && typeof r.rms === 'number' && r.rms > 0) db = 20 * Math.log10(r.rms)
  if (db == null) return 0
  return Math.min(1, Math.max(0, (db - LEVEL_FLOOR_DB) / -LEVEL_FLOOR_DB))
}

export function createNativeSessionAudioRecorder(
  options: NativeSessionAudioRecorderOptions = {},
): NativeSessionAudioRecorder {
  const now = options.now ?? (() => Date.now())
  const listeners = new Set<(event: RecorderEvent) => void>()
  let snapshot: RecorderSnapshot = IDLE_RECORDER_SNAPSHOT
  /** The production recording this process started or adopted (events are only ours then). */
  let ownedId: string | null = null
  let lastLevelAt = 0
  /** While `start` runs, status reads of the previous recording must not reset the snapshot. */
  let startInFlight = false
  let stopCallWatch: (() => void) | null = null
  let pausedForCall = false
  /** Interruption log entry (by `atMs`) → reason decided when it began (stable after the call ends). */
  const interruptionReasons = new Map<number, RecorderInterruptionReason>()
  const nativeOffs: Array<() => void> = []

  const emit = (event: RecorderEvent) => {
    for (const listener of listeners) {
      try {
        listener(event)
      } catch {
        /* a listener's bug must not break the recorder */
      }
    }
  }

  const ascendCallEngaged = () => isCallBlockingRecording(getVoiceCallSession()?.phase ?? null)

  function reasonForType(type: string, status: RecorderStatus | null): RecorderInterruptionReason {
    switch (type) {
      case 'ascend-call':
        return 'ascend_call'
      case 'call':
        // Android sees every call as an audio-mode change; iOS's CallKit observer sees ours too.
        return ascendCallEngaged() ? 'ascend_call' : 'phone_call'
      case 'session-interruption':
      case 'interruption':
        return 'system_interruption'
      case 'low-storage':
        return 'low_storage'
      case 'route-change':
        return 'route_change'
      case 'write-error': {
        const free = status?.freeStorageBytes
        return free != null && free < SESSION_RECORDING_MIN_FREE_BYTES ? 'low_storage' : 'mic_lost'
      }
      default:
        // open-failed, capture-died, engine-stopped, media-services…
        return 'mic_lost'
    }
  }

  function rememberReason(entry: RecorderInterruption, status: RecorderStatus | null): RecorderInterruptionReason {
    const known = interruptionReasons.get(entry.atMs)
    if (known) return known
    const reason = reasonForType(entry.type, status)
    interruptionReasons.set(entry.atMs, reason)
    return reason
  }

  /** The device-side cause of a non-user pause: the active blocker, else the last pausing entry. */
  function pausingInterruption(status: RecorderStatus): RecorderInterruptionReason {
    for (let i = status.interruptions.length - 1; i >= 0; i -= 1) {
      const entry = status.interruptions[i]
      if (!entry.pausedRecording || entry.type === 'user-pause') continue
      if (status.activeInterruption && entry.type !== status.activeInterruption) continue
      return rememberReason(entry, status)
    }
    return status.activeInterruption
      ? reasonForType(status.activeInterruption, status)
      : 'system_interruption'
  }

  function toSnapshot(status: RecorderStatus): RecorderSnapshot {
    if (status.scope !== 'production' || !status.recordingId || status.state === 'idle') {
      return IDLE_RECORDER_SNAPSHOT
    }
    const state: RecorderSnapshot['state'] =
      status.state === 'interrupted-by-kill' ? 'stopped' : status.state
    const paused = state === 'paused'
    return {
      state,
      recordingId: status.recordingId,
      startedAtMs: status.startedAtMs,
      elapsedAudioSec: Math.floor(status.elapsedAudioMs / 1000),
      pauseReason:
        paused && status.userPaused
          ? status.userPauseReason === 'someone_joined'
            ? 'someone_joined'
            : 'private_moment'
          : null,
      interruption: paused && !status.userPaused ? pausingInterruption(status) : null,
      capReached: status.capReached,
      lastError: status.lastError ? MESSAGES.deviceProblem : null,
    }
  }

  function setSnapshot(next: RecorderSnapshot): RecorderSnapshot {
    const prev = snapshot
    if (sameSnapshot(prev, next)) return prev
    snapshot = next
    emit({ type: 'state', snapshot: next })
    if (prev.state === 'paused' && next.state === 'recording' && prev.recordingId === next.recordingId) {
      emit({ type: 'resumed', atMs: now() })
    }
    if (!ACTIVE_STATES.has(next.state)) unwatchCalls()
    return next
  }

  function applyStatus(status: RecorderStatus): RecorderSnapshot {
    const next = toSnapshot(status)
    if (startInFlight && next.recordingId !== ownedId) return snapshot
    if (next.recordingId && ACTIVE_STATES.has(next.state) && ownedId !== next.recordingId) {
      // Live production capture this process didn't start (WebView reload): adopt it.
      ownedId = next.recordingId
      watchCalls()
    }
    if (next.recordingId && next.recordingId !== ownedId && !ACTIVE_STATES.has(next.state)) {
      return setSnapshot(IDLE_RECORDER_SNAPSHOT)
    }
    return setSnapshot(next)
  }

  async function refresh(): Promise<RecorderSnapshot> {
    if (!isSessionRecorderAvailable()) return snapshot
    try {
      return applyStatus(await getRecorderStatus('production'))
    } catch {
      return snapshot
    }
  }

  function watchCalls(): void {
    if (stopCallWatch) return
    pausedForCall = false
    const check = () => {
      const engaged = ascendCallEngaged()
      if (engaged && snapshot.state === 'recording' && !pausedForCall) {
        pausedForCall = true
        void pauseRecorder('ascend-call')
          .then(applyStatus)
          .catch(() => undefined)
      } else if (!engaged) {
        // The call ended: stay paused — the provider resumes (no auto-resume).
        pausedForCall = false
      }
    }
    stopCallWatch = subscribeVoiceCallSession(check)
    check()
  }

  function unwatchCalls(): void {
    stopCallWatch?.()
    stopCallWatch = null
    pausedForCall = false
  }

  function ours(): boolean {
    return ownedId != null && ACTIVE_STATES.has(snapshot.state)
  }

  function attachNative(): void {
    if (!isSessionRecorderAvailable() || nativeOffs.length > 0) return
    nativeOffs.push(
      onRecorderEvent('state', () => void refresh()),
      onRecorderEvent('chunk', () => void refresh()),
      onRecorderEvent<RecorderInterruption>('interruption', (entry) => {
        if (
          ours() &&
          entry &&
          entry.pausedRecording === true &&
          entry.type !== 'user-pause' &&
          typeof entry.resumedAtMs !== 'number'
        ) {
          emit({
            type: 'interrupted',
            reason: rememberReason(
              { ...entry, atMs: typeof entry.atMs === 'number' ? entry.atMs : now() },
              null,
            ),
            atMs: typeof entry.atMs === 'number' ? entry.atMs : now(),
          })
        }
        void refresh()
      }),
      onRecorderEvent('level', (data) => {
        if (!ours() || snapshot.state !== 'recording') return
        const at = now()
        if (at - lastLevelAt < LEVEL_MIN_INTERVAL_MS) return
        lastLevelAt = at
        emit({ type: 'level', level: normalizeRecorderLevel(data) })
      }),
      onRecorderEvent('capReached', () => {
        if (ownedId == null) return
        emit({ type: 'capReached', atMs: now() })
        void refresh()
      }),
      onRecorderEvent<{ fatal?: unknown }>('error', (data) => {
        if (ownedId == null) return
        emit({ type: 'error', code: data?.fatal === true ? 'recorder_failed' : 'write_failed' })
        void refresh()
      }),
    )
  }

  function startErrorMessage(err: unknown): string {
    if (isSessionRecorderUnavailableError(err)) return MESSAGES.updateApp
    const code = errorCode(err)
    const text = errorText(err).toLowerCase()
    if (code === 'MIC_PERMISSION' || text.includes('permission')) return MESSAGES.micPermission
    if (code === 'BUSY' || text.includes('already in progress')) return MESSAGES.busy
    if (text.includes('already has audio')) return MESSAGES.alreadyHasAudio
    if (text.includes('during a call')) return MESSAGES.callActive
    if (text.includes('storage')) return MESSAGES.lowStorage
    return MESSAGES.startFailed
  }

  const recorder: NativeSessionAudioRecorder = {
    kind: 'native',

    isSupported: () => isSessionRecorderAvailable(),

    async checkReadiness(): Promise<RecorderReadiness> {
      if (!isSessionRecorderAvailable()) {
        return { ok: false, reason: 'unsupported', message: MESSAGES.unsupported }
      }
      if (ascendCallEngaged()) return { ok: false, reason: 'call_active', message: MESSAGES.callActive }
      let native
      try {
        native = await getRecorderReadiness()
        if (native.active) return { ok: false, reason: 'busy', message: MESSAGES.busy }
        if (native.callActive) return { ok: false, reason: 'call_active', message: MESSAGES.callActive }
        if (native.micPermission === 'prompt') {
          // Ask now (before consent), not after the server recording exists.
          native = await getRecorderReadiness({ requestPermission: true })
        }
      } catch (err) {
        return {
          ok: false,
          reason: 'unsupported',
          message: isSessionRecorderUnavailableError(err) ? MESSAGES.updateApp : MESSAGES.startFailed,
        }
      }
      if (native.micPermission !== 'granted') {
        return { ok: false, reason: 'mic_permission', message: MESSAGES.micPermission }
      }
      if (native.freeStorageBytes != null && native.freeStorageBytes < SESSION_RECORDING_MIN_FREE_BYTES) {
        return { ok: false, reason: 'low_storage', message: MESSAGES.lowStorage }
      }
      return { ok: true }
    },

    async start({ recordingId, maxSeconds }) {
      if (!isSessionRecorderAvailable()) throw new Error(MESSAGES.unsupported)
      if (ascendCallEngaged()) throw new Error(MESSAGES.callActive)
      attachNative()
      const format = SESSION_RECORDING_AUDIO_FORMAT
      ownedId = recordingId
      interruptionReasons.clear()
      setSnapshot({ ...IDLE_RECORDER_SNAPSHOT, state: 'starting', recordingId, startedAtMs: now() })
      let status: RecorderStatus
      startInFlight = true
      try {
        status = await startProductionRecorder({
          recordingId,
          sampleRate: format.sampleRate,
          bitrate: format.bitrate,
          segmentSeconds: format.segmentSeconds,
          maxSeconds,
          maxBytes: SESSION_RECORDING_LOCAL_MAX_BYTES,
        })
      } catch (err) {
        startInFlight = false
        ownedId = null
        setSnapshot(IDLE_RECORDER_SNAPSHOT)
        throw new Error(startErrorMessage(err))
      }
      startInFlight = false
      applyStatus(status)
      watchCalls()
    },

    async pause(reason) {
      if (!isSessionRecorderAvailable() || !ours()) return
      applyStatus(await pauseRecorder(reason))
    },

    async resume() {
      if (!isSessionRecorderAvailable() || !ours()) return
      if (ascendCallEngaged()) throw new Error(MESSAGES.resumeDuringCall)
      const native = await getRecorderReadiness().catch(() => null)
      if (native?.callActive) throw new Error(MESSAGES.resumeDuringCall)
      const next = applyStatus(await resumeRecorder())
      if (next.state !== 'recording') throw new Error(MESSAGES.micNotBack)
    },

    async stop() {
      if (!isSessionRecorderAvailable()) throw new Error(MESSAGES.unsupported)
      const status = await stopRecorder()
      const recordingId = status.scope === 'production' ? status.recordingId : null
      if (!recordingId || (ownedId != null && recordingId !== ownedId)) {
        throw new Error(MESSAGES.nothingRecording)
      }
      ownedId = recordingId
      applyStatus(status)
      unwatchCalls()
      return { recordingId, durationSec: Math.round(status.elapsedAudioMs / 1000) }
    },

    async discardLocal(recordingId) {
      if (!isSessionRecorderAvailable()) return
      try {
        await deleteProductionRecording(recordingId)
      } catch (err) {
        if (!isSessionRecorderUnavailableError(err)) throw err
      }
      if (ownedId === recordingId) {
        ownedId = null
        unwatchCalls()
        setSnapshot(IDLE_RECORDER_SNAPSHOT)
      }
    },

    status: () => refresh(),

    async findOrphans(): Promise<OrphanRecording[]> {
      if (!isSessionRecorderAvailable()) return []
      let stored
      try {
        stored = await listStoredRecordings('production')
      } catch {
        return []
      }
      const out: OrphanRecording[] = []
      for (const rec of stored) {
        if (rec.live || rec.recordingId === ownedId) continue
        if (rec.chunkCount === 0 && rec.joinedBytes === 0) continue
        if (options.isTracked && (await options.isTracked(rec.recordingId))) continue
        out.push({
          recordingId: rec.recordingId,
          startedAtMs: rec.startedAtMs,
          elapsedAudioSec: Math.floor(rec.elapsedAudioMs / 1000),
        })
      }
      return out.sort((a, b) => a.startedAtMs - b.startedAtMs)
    },

    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },

    dispose() {
      for (const off of nativeOffs.splice(0)) off()
      unwatchCalls()
      listeners.clear()
    },
  }

  attachNative()
  void refresh()
  return recorder
}
